import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import {
  clearSsoPending,
  exchangeSsoLogin,
  parseSsoCallback,
  storeSsoPending,
  stripSsoParamsFromLocation,
  SsoApiError,
} from "./api";

export function SsoCallback({
  search,
  hash,
  token,
  onSession,
  onNeedLink,
}: {
  search: string;
  hash?: string;
  token?: string | null;
  onSession: (session: unknown) => void;
  onNeedLink?: (code: string, tenant: string | null) => void;
}): JSX.Element | null {
  const parsed = parseSsoCallback(`${search}${hash ?? ""}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const completion = useRef(onSession);
  completion.current = onSession;
  const request = useRef<{code: string; promise: Promise<unknown>} | null>(null);

  useEffect(() => {
    if (!parsed) return;
    storeSsoPending(parsed);
    stripSsoParamsFromLocation();
    if (parsed.kind !== "exchange") return;
    let cancelled = false;
    setBusy(true);
    if (request.current?.code !== parsed.code) request.current = {code:parsed.code,promise:exchangeSsoLogin(parsed.code)};
    void request.current.promise
      .then((session) => {
        if (cancelled) return;
        clearSsoPending();
        completion.current(session);
      })
      .catch((err) => { if (!cancelled) setError(err instanceof SsoApiError ? err.message : "SSOの完了に失敗しました"); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [parsed?.kind, parsed?.code]);

  if (!parsed) return null;
  if (parsed.kind === "link") {
    return (
      <div className="space-y-3 rounded-md border border-border-default p-4" role="status">
        <p>IdPの本人は確認できました。このブラウザーでメールまたはPasskeyによりログインし、紐付けを承認してください。</p>
        <Button type="button" onClick={() => onNeedLink?.(parsed.code, parsed.tenant)}>紐付けを続ける</Button>
        {token && <p className="text-sm text-fg-secondary">ログイン済みです。設定のSSO欄から承認できます。</p>}
      </div>
    );
  }
  return (
    <div role="status" className="space-y-2">
      {busy && <p>SSOを完了しています…</p>}
      {error && <p role="alert" className="text-danger">{error}</p>}
    </div>
  );
}
